import { MediaImage, Spread } from 'editor/src/store/design/types';

// A gang sheet list row: every copy of one artwork at one size. Copies of the same
// artwork resized to a different size form a separate group (and a separate row).
export interface ArtworkGroup {
  key: string;
  imageId: string;
  // un-rotated element size, mm
  widthMm: number;
  heightMm: number;
  // canvas elements of this group, in element order
  elements: MediaImage[];
}

// 0.1mm resolution: interactive resizes produce long fractions that would otherwise
// split visually-identical copies into separate groups
function roundMm(value: number): number {
  return Math.round(value * 10) / 10;
}

export function getArtworkGroupKey(element: MediaImage): string {
  return `${element.imageId}|${roundMm(element.width)}x${roundMm(element.height)}`;
}

// Media image elements of the sheet (first page of the spread) grouped by artwork and
// size, in first-appearance order. The canvas is the single source of truth for the
// gang sheet list: removing an element removes it from its group, duplicating adds.
function getArtworkGroups(spread: Spread | undefined): ArtworkGroup[] {
  const groups: ArtworkGroup[] = [];
  const groupsByKey = new Map<string, ArtworkGroup>();

  spread?.pages[0]?.groups.media?.forEach((element) => {
    // emptied image frames (placeholder without an artwork) are not gang sheet copies
    if (element.type !== 'image' || !element.imageId) {
      return;
    }
    const key = getArtworkGroupKey(element);
    const group = groupsByKey.get(key);
    if (group) {
      group.elements.push(element);
    } else {
      const newGroup: ArtworkGroup = {
        key,
        imageId: element.imageId,
        widthMm: roundMm(element.width),
        heightMm: roundMm(element.height),
        elements: [element],
      };
      groupsByKey.set(key, newGroup);
      groups.push(newGroup);
    }
  });

  return groups;
}

export default getArtworkGroups;
